import React, { useState } from "react";
import {
  SafeAreaView,
  FlatList,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { Colors } from "@/constants/Colors";
import { FontFamilies } from "@/constants/Typography";
import { UploadBillDialog } from "@/components/bills/UploadBillDialog";
import { BillItem } from "@/components/bills/BillItem";
import { useBills } from "@/hooks/useBills";
import { UserBill } from "@/types/bill";
import { showError } from "@/utils/toast";
import Header from "@/components/ui/Header";
import { router, useFocusEffect } from "expo-router";
import FBTrackingService from "@/services/fbTrackingService";

const BillsScreen = () => {
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const { bills, isLoading, refetch } = useBills();

  // Refresh bills when screen comes into focus and track pageview
  useFocusEffect(
    React.useCallback(() => {
      FBTrackingService.trackViewContent("bills");
      refetch();
    }, [refetch])
  );

  const handleBillPress = (bill: UserBill) => {
    if (!bill.accountNumber || !bill.billingDate) {
      showError("Account number or billing date not available for this bill");
      return;
    }
    router.push(
      `/bills/${encodeURIComponent(
        `${bill.accountNumber}#${bill.billingDate}`
      )}`
    );
  };

  const handleUploadSuccess = () => {
    // Refresh the bills list after successful upload
    refetch();
  };

  return (
    <SafeAreaView style={styles.container}>
      <Header title="Bills" />

      {isLoading ? (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={Colors.primary} />
          <Text style={styles.loadingText}>Loading bills...</Text>
        </View>
      ) : (
        <FlatList
          data={bills}
          renderItem={({ item }) => (
            <BillItem bill={item} onPress={() => handleBillPress(item)} />
          )}
          keyExtractor={(item, index) =>
            `${item.accountNumber}-${item.billingDate}` || index.toString()
          }
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.content}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons
                name="document-text-outline"
                size={64}
                color="#9CA3AF"
              />
              <Text style={styles.emptyTitle}>No Bills Uploaded</Text>
              <Text style={styles.emptySubtitle}>
                Upload your bills to track and initiate refunds
              </Text>
              <TouchableOpacity
                style={styles.uploadButton}
                onPress={() => setShowUploadDialog(true)}
              >
                <Ionicons name="cloud-upload-outline" size={24} color="#FFF" />
                <Text style={styles.uploadButtonText}>
                  Upload Your First Bill
                </Text>
              </TouchableOpacity>
            </View>
          }
        />
      )}

      {/* Floating Action Button */}
      {bills.length > 0 && (
        <TouchableOpacity
          style={styles.fab}
          onPress={() => setShowUploadDialog(true)}
        >
          <Ionicons name="add" size={28} color="#FFF" />
        </TouchableOpacity>
      )}

      <UploadBillDialog
        visible={showUploadDialog}
        onClose={() => {
          setShowUploadDialog(false);
          handleUploadSuccess();
        }}
      />
    </SafeAreaView>
  );
};

export default BillsScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },
  separator: {
    height: 12,
  },
  centerContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 100,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: "#6B7280",
    fontFamily: FontFamilies.Inter.Regular,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 100,
    paddingHorizontal: 32,
  },
  emptyTitle: {
    fontSize: 20,
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#1F2937",
    marginTop: 16,
  },
  emptySubtitle: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#6B7280",
    textAlign: "center",
    marginTop: 8,
    marginBottom: 24,
  },
  uploadButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#0F9129",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
  },
  uploadButtonText: {
    color: "#FFF",
    fontSize: 16,
    fontFamily: FontFamilies.Inter.Medium,
    marginLeft: 8,
  },
  fab: {
    position: "absolute",
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: "#0F9129",
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
});
